/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * PhoneInput — Smart phone input with auto-detection and validation.
 *
 * - Regular editable input — user can type ANY international number
 * - Placeholder shows the org's country format as a guide
 * - Auto-detects country from dialed prefix (e.g., +224 → 🇬🇳 Guinea)
 * - "00" at start auto-converts to "+"
 * - Auto-formats with spaces as user types
 * - ✓ green check when digit count matches detected country format
 * - ✗ red cross when complete but digit count is wrong
 * - Flag updates dynamically based on detected country
 */

import { useState, useEffect, useRef, useMemo } from 'react';

interface PhoneInputProps {
  /** Placeholder mask from org's country config, e.g. "+224 6XX XXX XXX" */
  mask?: string;
  /** Default country code for flag when input is empty, e.g. "GN" */
  countryCode?: string;
  value: string;
  onChange: (formatted: string) => void;
  className?: string;
  error?: string;
  label?: string;
  readOnly?: boolean;
  name?: string;
}

// ── Country detection from dial code ─────────────────────────────────────────

interface DialEntry { dial: string; code: string; digits: number }

// Sorted longest-first so +1 doesn't match before +1XXX area codes
const DIAL_TABLE: DialEntry[] = [
  { dial: '224', code: 'GN', digits: 9 },
  { dial: '223', code: 'ML', digits: 8 },
  { dial: '221', code: 'SN', digits: 9 },
  { dial: '225', code: 'CI', digits: 10 },
  { dial: '226', code: 'BF', digits: 8 },
  { dial: '227', code: 'NE', digits: 8 },
  { dial: '228', code: 'TG', digits: 8 },
  { dial: '229', code: 'BJ', digits: 8 },
  { dial: '233', code: 'GH', digits: 9 },
  { dial: '234', code: 'NG', digits: 10 },
  { dial: '245', code: 'GW', digits: 7 },
  { dial: '220', code: 'GM', digits: 7 },
  { dial: '232', code: 'SL', digits: 8 },
  { dial: '231', code: 'LR', digits: 9 },
  { dial: '238', code: 'CV', digits: 7 },
  { dial: '237', code: 'CM', digits: 9 },
  { dial: '241', code: 'GA', digits: 8 },
  { dial: '242', code: 'CG', digits: 9 },
  { dial: '243', code: 'CD', digits: 9 },
  { dial: '212', code: 'MA', digits: 9 },
  { dial: '213', code: 'DZ', digits: 9 },
  { dial: '216', code: 'TN', digits: 8 },
  { dial: '20', code: 'EG', digits: 10 },
  { dial: '27', code: 'ZA', digits: 9 },
  { dial: '254', code: 'KE', digits: 9 },
  { dial: '250', code: 'RW', digits: 9 },
  { dial: '251', code: 'ET', digits: 9 },
  { dial: '255', code: 'TZ', digits: 9 },
  { dial: '256', code: 'UG', digits: 9 },
  { dial: '33', code: 'FR', digits: 9 },
  { dial: '44', code: 'GB', digits: 10 },
  { dial: '49', code: 'DE', digits: 11 },
  { dial: '971', code: 'AE', digits: 9 },
  { dial: '966', code: 'SA', digits: 9 },
  { dial: '91', code: 'IN', digits: 10 },
  { dial: '86', code: 'CN', digits: 11 },
  { dial: '1', code: 'US', digits: 10 },
].sort((a, b) => b.dial.length - a.dial.length);

function detectCountry(digits: string): { code: string; expectedTotal: number } | null {
  for (const entry of DIAL_TABLE) {
    if (digits.startsWith(entry.dial)) {
      return { code: entry.code, expectedTotal: entry.dial.length + entry.digits };
    }
  }
  return null;
}

// ── Flag from ISO code ───────────────────────────────────────────────────────

function countryFlag(iso?: string): string {
  if (!iso || iso.length !== 2) return '';
  const upper = iso.toUpperCase();
  const cp1 = 0x1F1E6 + upper.charCodeAt(0) - 65;
  const cp2 = 0x1F1E6 + upper.charCodeAt(1) - 65;
  return String.fromCodePoint(cp1, cp2);
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function extractDigits(s: string): string { return s.replace(/\D/g, ''); }

/** Format a digit string with spaces for readability: +XXX XX XXX XXXX */
function autoFormat(raw: string): string {
  let s = raw.trim();
  // "00" prefix → "+"
  if (s.startsWith('00')) s = '+' + s.slice(2);
  // Ensure starts with +
  if (s.length > 0 && !s.startsWith('+')) {
    const d = extractDigits(s);
    s = '+' + d;
  }
  if (s === '+') return '+';

  const digits = extractDigits(s);
  if (digits.length === 0) return '';

  // Format: +XXX XX XXX XXXX (adaptive grouping)
  let result = '+';
  for (let i = 0; i < digits.length; i++) {
    // Add space after country code (2-3 digits), then every 3 digits
    if (i === 3 || (i > 3 && (i - 3) % 3 === 0)) result += ' ';
    result += digits[i];
  }
  return result;
}

// ── Component ────────────────────────────────────────────────────────────────

export function PhoneInput({
  mask,
  countryCode,
  value,
  onChange,
  className = '',
  error,
  label,
  readOnly = false,
  name,
}: PhoneInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [raw, setRaw] = useState(value || '');

  useEffect(() => { setRaw(value || ''); }, [value]);

  const digits = extractDigits(raw);
  const detected = useMemo(() => detectCountry(digits), [digits]);
  const flagIso = detected?.code || countryCode;
  const flag = countryFlag(flagIso);

  // Validation state
  const hasContent = digits.length > 0;
  // Max digits: detected country's expected total, or 15 (ITU max) if unknown
  const maxDigits = detected?.expectedTotal ?? 15;
  const isComplete = digits.length === maxDigits || (!detected && digits.length >= 7);
  const showCheck = hasContent && isComplete;

  /** Cap digits to max and format */
  const capAndFormat = (v: string): string => {
    if (v.startsWith('00')) v = '+' + v.slice(2);
    v = v.replace(/[^\d+ ]/g, '');
    const d = extractDigits(v);
    // Re-detect with new digits to get accurate max
    const det = detectCountry(d);
    const max = det?.expectedTotal ?? 15;
    const capped = d.slice(0, max);
    // Rebuild with + prefix
    return autoFormat('+' + capped);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = capAndFormat(e.target.value);
    setRaw(formatted);
    onChange(formatted);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'].includes(e.key)) return;
    if (e.key === '+' && raw.length === 0) return;
    // Block new digits if at max
    if (/^\d$/.test(e.key) && digits.length >= maxDigits) { e.preventDefault(); return; }
    if (/^\d$/.test(e.key)) return;
    if (e.key === ' ') return;
    e.preventDefault();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    let pasted = e.clipboardData.getData('text');
    const formatted = capAndFormat(pasted);
    setRaw(formatted);
    onChange(formatted);
  };

  // Build placeholder from mask (replace X with the digit position hint)
  const placeholder = mask || '+XXX XXX XXX XXX';

  return (
    <div className={`phi ${className} ${error ? 'phi--err' : ''} ${readOnly ? 'phi--ro' : ''}`}>
      {label && <label className="phi__label">{label}</label>}
      <div className={`phi__field ${showCheck ? 'phi__field--ok' : ''}`}>
        {/* Dynamic flag badge */}
        <div className="phi__badge">
          {flag ? (
            <span className="phi__flag">{flag}</span>
          ) : (
            <span className="phi__flag-icon">{'\u{1F4DE}'}</span>
          )}
        </div>
        <div className="phi__sep" />
        {/* The actual editable input */}
        <input
          ref={inputRef}
          type="tel"
          name={name}
          className="phi__input"
          value={raw}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder}
          readOnly={readOnly}
          autoComplete="tel"
        />
        {/* Validation icon */}
        {showCheck && <span className="phi__check">{'\u2713'}</span>}
      </div>
      {error && <span className="phi__error">{error}</span>}
      <style>{CSS}</style>
    </div>
  );
}

const CSS = `
.phi { display: flex; flex-direction: column; gap: 6px; }
.phi__label { font-size: 0.8125rem; font-weight: 500; color: var(--color-text-secondary); }
.phi__error { font-size: 0.75rem; color: var(--color-error); }

.phi__field {
  display: flex;
  align-items: center;
  height: 40px;
  background: var(--input-bg, var(--color-bg-primary));
  border: 1px solid var(--input-border, var(--color-border-default));
  border-radius: var(--input-radius, var(--radius-md));
  transition: border-color 0.15s, box-shadow 0.15s;
  overflow: hidden;
}
.phi__field:focus-within {
  border-color: var(--color-brand-primary);
  box-shadow: 0 0 0 2px var(--input-focus-ring, rgba(59,130,246,0.12));
}
.phi__field--ok { border-color: var(--color-success); }
.phi__field--ok:focus-within { border-color: var(--color-success); box-shadow: 0 0 0 2px rgba(0,200,100,0.12); }
.phi__field--bad { border-color: var(--color-error); }
.phi--err .phi__field { border-color: var(--color-error) !important; }
.phi--ro .phi__field { opacity: 0.65; cursor: default; background: var(--color-bg-tertiary); }

/* Flag badge */
.phi__badge {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  height: 100%;
  background: var(--color-bg-tertiary);
  user-select: none;
  flex-shrink: 0;
  min-width: 40px;
}
.phi__flag { font-size: 18px; line-height: 1; }
.phi__flag-icon { font-size: 16px; line-height: 1; opacity: 0.5; }

.phi__sep {
  width: 1px;
  height: 60%;
  background: var(--color-border-subtle);
  flex-shrink: 0;
}

/* Editable input */
.phi__input {
  flex: 1;
  height: 100%;
  padding: 0 10px;
  border: none;
  background: transparent;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.875rem;
  letter-spacing: 0.8px;
  color: var(--color-text-primary);
  outline: none;
}
.phi__input::placeholder {
  color: var(--color-text-muted);
  opacity: 0.4;
  letter-spacing: 1px;
}

/* Validation icons */
.phi__check {
  flex-shrink: 0;
  width: 22px; height: 22px;
  border-radius: 50%;
  background: var(--color-success);
  color: white;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700;
  margin-right: 8px;
  animation: phi-pop 0.2s cubic-bezier(0.34,1.56,0.64,1);
}
.phi__cross {
  flex-shrink: 0;
  width: 22px; height: 22px;
  border-radius: 50%;
  background: var(--color-error);
  color: white;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700;
  margin-right: 8px;
  animation: phi-pop 0.2s cubic-bezier(0.34,1.56,0.64,1);
}
@keyframes phi-pop { 0% { transform: scale(0); } 100% { transform: scale(1); } }

@media (max-width: 480px) {
  .phi__input { font-size: 0.8125rem; }
}
`;
